import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

export interface PatientTab {
  id: string;
  label: string; // short display name, e.g. "A.Sagar"
  fullName?: string; // full patient name, shown when tab is active
  initials: string;
  color: string;
  photoUrl?: string | null;
}

interface PatientTabsContextValue {
  tabs: PatientTab[];
  activePatientId: string | null;
  pendingTab: PatientTab | null;
  openPatientTab: (tab: PatientTab) => void;
  closePatientTab: (id: string) => void;
  setActivePatientId: (id: string | null) => void;
  setMaxTabs: (max: number) => void;
  clearPendingTab: () => void;
  confirmPendingTab: (closeIds: string[]) => void;
}

const PatientTabsContext = createContext<PatientTabsContextValue | null>(null);

const AVATAR_COLORS = [
  '#f97316',
  '#3b82f6',
  '#a855f7',
  '#22c55e',
  '#ef4444',
  '#14b8a6',
  '#f59e0b',
  '#6366f1',
  '#ec4899',
  '#0ea5e9',
];

function hashCode(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

export function getPatientColor(id: string): string {
  return AVATAR_COLORS[hashCode(id) % AVATAR_COLORS.length];
}

export function getInitialsShort(fullName: string): string {
  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + '.' + parts[parts.length - 1][0]).toUpperCase();
}

export function getShortLabel(fullName: string): string {
  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'Unknown';
  if (parts.length === 1) return parts[0];
  return `${parts[0][0]}.${parts[parts.length - 1]}`;
}

export function PatientTabsProvider({ children }: { children: ReactNode }) {
  const [tabs, setTabs] = useState<PatientTab[]>([]);
  const [activePatientId, setActivePatientId] = useState<string | null>(null);
  const [pendingTab, setPendingTab] = useState<PatientTab | null>(null);
  const maxTabsRef = useRef(Infinity);

  const setMaxTabs = useCallback((max: number) => {
    maxTabsRef.current = max;
  }, []);

  const openPatientTab = useCallback((tab: PatientTab) => {
    let atCapacity = false;
    setTabs((prev) => {
      const existing = prev.find((t) => t.id === tab.id);
      if (existing) {
        if (
          existing.label !== tab.label ||
          existing.fullName !== tab.fullName ||
          existing.initials !== tab.initials ||
          existing.color !== tab.color ||
          existing.photoUrl !== tab.photoUrl
        ) {
          return prev.map((t) =>
            t.id === tab.id
              ? {
                  ...t,
                  label: tab.label,
                  fullName: tab.fullName,
                  initials: tab.initials,
                  color: tab.color,
                  photoUrl: tab.photoUrl,
                }
              : t,
          );
        }
        return prev;
      }
      if (prev.length >= maxTabsRef.current) {
        atCapacity = true;
        return prev;
      }
      return [...prev, tab];
    });
    if (atCapacity) {
      setPendingTab(tab);
    } else {
      setActivePatientId(tab.id);
    }
  }, []);

  const clearPendingTab = useCallback(() => {
    setPendingTab(null);
  }, []);

  const confirmPendingTab = useCallback((closeIds: string[]) => {
    setPendingTab((pending) => {
      if (!pending) return null;
      setTabs((prev) => {
        const filtered = prev.filter((t) => !closeIds.includes(t.id));
        return [...filtered, pending];
      });
      setActivePatientId(pending.id);
      return null;
    });
  }, []);

  const closePatientTab = useCallback((id: string) => {
    setTabs((prev) => prev.filter((t) => t.id !== id));
    setActivePatientId((prev) => (prev === id ? null : prev));
  }, []);

  return (
    <PatientTabsContext.Provider
      value={{
        tabs,
        activePatientId,
        pendingTab,
        openPatientTab,
        closePatientTab,
        setActivePatientId,
        setMaxTabs,
        clearPendingTab,
        confirmPendingTab,
      }}
    >
      {children}
    </PatientTabsContext.Provider>
  );
}

export function usePatientTabs(): PatientTabsContextValue {
  const ctx = useContext(PatientTabsContext);
  if (!ctx) throw new Error('usePatientTabs must be used within PatientTabsProvider');
  return ctx;
}
